import { prepAutocompleteData, serialize } from '@/services/UtilityService';
import ApiFactory from '@/factories/ApiFactory';
import { B2B_EDIT_COMPANY, checkHasPermission } from '@/middlewares/permissions';

const getCompanyList = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posB2B/v1/companies?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getB2BBusinesses = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posB2B/v1/businesses?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const autocomplete = (q, labelProp = 'restaurantName', valueProp = 'restaurantID') => {
	return new Promise((resolve) => {
		getB2BBusinesses(q).then((data) => {
			resolve(prepAutocompleteData(data, labelProp, valueProp));
		});
	});
};

const companyAutocomplete = (q, labelProp = 'restaurantName', valueProp = 'restaurantID') => {
	return new Promise((resolve) => {
		getCompanyList(q).then((data) => {
			resolve(prepAutocompleteData(data, labelProp, valueProp));
		});
	});
};

const employeeAutocomplete = (q, labelProp = 'firstName', valueProp = 'id') => {
	return new Promise((resolve) => {
		getEmployeeList(q).then((data) => {
			resolve(prepAutocompleteData(data, labelProp, valueProp));
		});
	});
};

const getCompanyDetails = (id) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posB2B/v1/companies/${id}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createOrUpdateCompanyDetails = (data) => {
	return new Promise((resolve, reject) => {
		let promise;
		if (data.restaurantID) {
			promise = ApiFactory.put(`posB2B/v1/companies/${data.restaurantID}`, data);
		} else {
			promise = ApiFactory.post(`posB2B/v1/companies`, data);
		}
		promise.then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCompanyTimeSlots = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posB2B/v1/b2B-time-slots`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createCompaniesOrderingTime = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posB2B/v1/companies/${companyId}/ordering-time`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateCompaniesOrderingTime = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/ordering-time`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createCompanyPreferredBusinesses = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posB2B/v1/companies/${companyId}/features`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateCompanyPreferredBusinesses = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/features`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createOrUpdateContract = (companyId, data) => {
	return new Promise((resolve, reject) => {
		let promise;
		if (data.id > 0) {
			promise = ApiFactory.put(`posB2B/v1/companies/${companyId}/contracts/${data.id}`, data);
		} else {
			promise = ApiFactory.post(`posB2B/v1/companies/${companyId}/contracts`, data);
		}
		promise.then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getEmployeeList = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`/posB2B/v1/companies/${filters.companyId}/employee?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createEmployee = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posB2B/v1/companies/${companyId}/employee`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateEmployee = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/employee/${payload.id}`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateEmployeeListStatus = (companyId, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/employee-list/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateEmployeeStatus = (companyId, employeeId, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/employee/${employeeId}/status/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getEmployeeGroupList = (companyId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/${companyId}/employee-group`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'name', 'id'));
		}, reject);
	});
};

const getEmployeeGroupStats = (filters, companyId) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`/posB2B/v1/companies/${companyId}/employee-group-stats?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateEmployeeGroup = (payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${payload.companyId}/employee-group/${payload.id}`, payload).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const createEmployeeGroup = (payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posB2B/v1/companies/${payload.companyId}/employee-group`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const deleteEmployeeGroup = (companyId, employeeGroupId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.delete(`posB2B/v1/companies/${companyId}/employee-group/${employeeGroupId}`).then(() => {
			resolve();
		}, reject);
	});
};

const updateEmployeeGroupStatus = (companyId, employeeGroupId, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posB2B/v1/companies/${companyId}/employee-groups/${employeeGroupId}/status/${status}`).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const getEmployeeStatuses = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/b2b-employee-statuses`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getAvailableCompaniesStatuses = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/statuses`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'value', 'id'));
		}, reject);
	});
};

const updateCompanyStatus = (id, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/posB2B/v1/companies/${id}/status/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const importEmployees = (payload, companyId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posB2B/v1/companies/${companyId}/employee/import`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const checkIfCustomerExists = (payload) => {
	const query = serialize(payload);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posB2B/v1/customer-exists?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getDashboardInfo = (companyId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posB2B/v1/b2b-dashboard/${companyId}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getFinanceDashboard = (companyId, filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posB2B/v1/b2b-finance-dashboard/${companyId}?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCompanyInvoices = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posB2B/v1/invoices?${query}`).then(({ data }) => {
			const invoices = data.data.map((i) => {
				return {
					...i,
					restaurantVat: i.restaurant['vat'],
					restaurantTotalPercentage:
						i.restaurant['baboonPercentage'] +
						i.restaurant['courierPercentage'] +
						i.restaurant['marketingPercentage'],
					subtotal: i['amountNoVatCents'] / 100,
					vat: i['vatCents'] / 100,
					total: (i['vatCents'] + i['amountNoVatCents']) / 100,
					paid: i['paidAmountCents'] / 100,
					unpaid: (i['vatCents'] + i['amountNoVatCents'] - i['paidAmountCents']) / 100,
				};
			});
			resolve(invoices);
		}, reject);
	});
};

const canEditCompany = () => {
	return checkHasPermission([B2B_EDIT_COMPANY]);
};

const getCompanyBudgets = (filters) => {
	const query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/${filters.companyId}/budgets?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCompanyBudgetDetails = (companyId, budgetId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/${companyId}/budgets/${budgetId}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getB2BPaymentMethods = (companyId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/${companyId}/payment_methods`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'value', 'value'));
		}, reject);
	});
};

const checkIfBudgetExists = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/posB2B/v1/companies/${companyId}/budgets/exists`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createBudget = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/posB2B/v1/companies/${companyId}/budgets`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateBudget = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/posB2B/v1/companies/${companyId}/budgets/${payload.id}`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCompanyBudgetStatuses = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/budget_statuses`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};

const saveBudgetAttachment = (companyId, budgetId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/posB2B/v1/companies/${companyId}/budgets/${budgetId}/attachment`, payload).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const deleteBudgetAttachment = (companyId, budgetId, attachmentId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.delete(`/posB2B/v1/companies/${companyId}/budget/${budgetId}/attachments/${attachmentId}`).then(
			({ data }) => {
				resolve(data.data);
			},
			reject
		);
	});
};

const changeBudgetStatus = (companyId, budgetId, status) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`/posB2B/v1/companies/${companyId}/budgets/${budgetId}/status/${status}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getFinanceAlert = (companyId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/${companyId}/alert`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const importEmployeeGroups = (companyId, payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`/posB2B/v1/companies/${companyId}/employee-group/import`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getCompanyOrdersDashboard = (filters) => {
	const query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/orders/dashboard?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getFinanceSituation = (filters) => {
	let query = serialize(filters);
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/accounting/${filters.accountId}/financial-situation?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getAccountingDocTypes = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`/posB2B/v1/companies/accounting/doc-types`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

export default {
	getCompanyList,
	autocomplete,
	companyAutocomplete,
	employeeAutocomplete,
	getB2BBusinesses,
	getCompanyDetails,
	createOrUpdateCompanyDetails,
	getCompanyTimeSlots,
	createCompaniesOrderingTime,
	updateCompaniesOrderingTime,
	createCompanyPreferredBusinesses,
	updateCompanyPreferredBusinesses,
	createOrUpdateContract,
	getEmployeeList,
	createEmployee,
	updateEmployee,
	updateEmployeeListStatus,
	updateEmployeeStatus,
	getEmployeeGroupList,
	getEmployeeGroupStats,
	updateEmployeeGroup,
	createEmployeeGroup,
	deleteEmployeeGroup,
	updateEmployeeGroupStatus,
	getEmployeeStatuses,
	getAvailableCompaniesStatuses,
	updateCompanyStatus,
	importEmployees,
	checkIfCustomerExists,
	getDashboardInfo,
	canEditCompany,
	getCompanyBudgets,
	getCompanyBudgetDetails,
	getB2BPaymentMethods,
	checkIfBudgetExists,
	createBudget,
	updateBudget,
	getCompanyBudgetStatuses,
	saveBudgetAttachment,
	deleteBudgetAttachment,
	changeBudgetStatus,
	getFinanceDashboard,
	getCompanyInvoices,
	getFinanceAlert,
	importEmployeeGroups,
	getCompanyOrdersDashboard,
	getFinanceSituation,
	getAccountingDocTypes,
};
